// Placeholder soldiers (2026-10-25), one merged geometry per class, until the 3D checkpoint replaces them with the
// real models. The spike's trick carries over: every vertex has a part id and the vertex shader swings the legs and the
// weapon from a per-instance (phase, state) pair, so each class is one instanced draw call and there is no skinning.
// Colors follow ROSTER.md: the army's base palette on the body (per instance), team color on one big area (the
// shield, banner, horse cloth), marked per vertex with aTc.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CLASSES, ClassId } from '../sim/defs';
import { ENAMEL, enamel } from './look';

const SKIN = 0xe8c4a0, METAL = 0xb9c0c8, WOOD = 0x8a5a32, DARK = 0x3a3330;

function part(g: THREE.BufferGeometry, id: number, color: number, tc = 0, at: [number, number, number] = [0, 0, 0], rot?: [number, number, number]) {
  const n = g.index ? g.toNonIndexed() : g;
  if (rot) n.rotateX(rot[0]).rotateY(rot[1]).rotateZ(rot[2]);
  n.translate(...at);
  const cnt = n.attributes.position.count, c = new THREE.Color(color);
  n.setAttribute('part', new THREE.Float32BufferAttribute(new Float32Array(cnt).fill(id), 1));
  n.setAttribute('aTc', new THREE.Float32BufferAttribute(new Float32Array(cnt).fill(tc), 1));
  n.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(cnt * 3).map((_, k) => [c.r, c.g, c.b][k % 3]), 3));
  n.deleteAttribute('uv');
  return n;
}
const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d);
const cyl = (r: number, h: number, s = 10) => new THREE.CylinderGeometry(r, r, h, s);

/** A soldier on foot: torso in the army color (white here, tinted per instance), skin head, two legs, a helmet. */
function infantry(scale = 1): THREE.BufferGeometry[] {
  return [
    part(box(0.5, 0.55, 0.32), 0, 0xffffff, 0, [0, 0.82, 0]),
    part(new THREE.SphereGeometry(0.17, 10, 8), 0, SKIN, 0, [0, 1.27, 0]),
    part(new THREE.SphereGeometry(0.2, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), 0, 0xffffff, 0, [0, 1.32, 0]),
    part(box(0.17, 0.55, 0.17), 1, DARK, 0, [0.13, 0.27, 0]),
    part(box(0.17, 0.55, 0.17), 2, DARK, 0, [-0.13, 0.27, 0]),
  ].map((g) => (scale !== 1 ? g.scale(scale, scale, scale) : g));
}
const shield = (r = 0.27) => part(cyl(r, 0.06, 14), 0, 0xffffff, 1, [-0.33, 0.85, 0.08], [0, 0, Math.PI / 2]);

const BUILD: Record<ClassId, () => THREE.BufferGeometry[]> = {
  footman: () => [...infantry(), shield(0.24), part(box(0.07, 0.07, 0.6), 3, METAL, 0, [0.33, 0.9, 0.28])],
  line: () => [...infantry(), shield(0.3), part(box(0.08, 0.08, 0.72), 3, METAL, 0, [0.33, 0.9, 0.32])],
  spear: () => [...infantry(), part(cyl(0.2, 0.05, 12), 0, 0xffffff, 1, [-0.31, 0.85, 0.06], [0, 0, Math.PI / 2]),
    part(box(0.05, 0.05, 2.4), 3, WOOD, 0, [0.3, 1.0, 0.55]), part(new THREE.ConeGeometry(0.06, 0.25, 6), 3, METAL, 0, [0.3, 1.0, 1.85], [Math.PI / 2, 0, 0])],
  ranged: () => [...infantry(), part(new THREE.ConeGeometry(0.24, 0.28, 10), 0, 0xffffff, 1, [0, 1.5, 0]),
    part(new THREE.TorusGeometry(0.42, 0.025, 4, 12, Math.PI), 3, WOOD, 0, [-0.32, 0.95, 0.15], [0, Math.PI / 2, Math.PI / 2])],
  cavalry: () => [
    part(box(0.5, 0.5, 1.3), 0, 0x8a6a4a, 0, [0, 0.85, 0]), part(box(0.56, 0.3, 1.0), 0, 0xffffff, 1, [0, 0.72, 0]),
    part(box(0.26, 0.5, 0.36), 0, 0x8a6a4a, 0, [0, 1.25, 0.72], [-0.5, 0, 0]),
    part(box(0.14, 0.6, 0.14), 1, 0x6a4a32, 0, [0.16, 0.3, 0.48]), part(box(0.14, 0.6, 0.14), 2, 0x6a4a32, 0, [-0.16, 0.3, 0.48]),
    part(box(0.14, 0.6, 0.14), 2, 0x6a4a32, 0, [0.16, 0.3, -0.48]), part(box(0.14, 0.6, 0.14), 1, 0x6a4a32, 0, [-0.16, 0.3, -0.48]),
    part(box(0.42, 0.5, 0.3), 0, 0xffffff, 0, [0, 1.35, -0.05]), part(new THREE.SphereGeometry(0.16, 10, 8), 0, SKIN, 0, [0, 1.75, -0.05]),
    part(new THREE.SphereGeometry(0.19, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), 0, 0xffffff, 0, [0, 1.8, -0.05]),
    part(box(0.05, 0.05, 2.0), 3, WOOD, 0, [0.28, 1.45, 0.6]),
  ],
  ram: () => BUILD.siege(), // a placeholder ram draws as the engine
  siege: () => [
    part(box(1.3, 0.35, 1.7), 0, WOOD, 0, [0, 0.5, 0]),
    ...[[-0.72, 0.6], [0.72, 0.6], [-0.72, -0.6], [0.72, -0.6]].map(([x, z]) => part(cyl(0.3, 0.12, 12), 0, DARK, 0, [x, 0.3, z], [0, 0, Math.PI / 2])),
    part(box(0.2, 0.9, 0.2), 0, WOOD, 0, [0, 1.1, -0.3]), part(box(0.12, 0.12, 2.0), 3, WOOD, 0, [0, 1.5, 0.2]),
    part(box(0.03, 0.5, 0.35), 0, 0xffffff, 1, [0.6, 1.0, -0.6]),
  ],
  support: () => [...infantry(), part(cyl(0.24, 0.3, 14), 0, 0xffffff, 1, [0, 0.8, 0.28], [Math.PI / 2, 0, 0]),
    part(box(0.04, 0.04, 0.35), 3, WOOD, 0, [0.25, 1.0, 0.3])],
  elite: () => [...infantry(1.18), part(cyl(0.34, 0.07, 14), 0, 0xffffff, 1, [-0.4, 1.0, 0.08], [0, 0, Math.PI / 2]),
    part(new THREE.ConeGeometry(0.12, 0.4, 6), 0, 0xffffff, 1, [0, 1.85, 0]), part(box(0.1, 0.1, 1.05), 3, METAL, 0, [0.4, 1.05, 0.42])],
  hero: () => BUILD.elite(), // unused on screen: main.ts draws a hero as its look's class (HERO[id].look)
};

export function unitGeometry(cls: ClassId) {
  return mergeGeometries(BUILD[cls]())!;
}

/** The shared material: base palette per instance (aBody) on white vertices, team color (instanceColor) where aTc = 1,
 *  legs and weapon swung by part id from aAnim = (phase, state). state 1 walk, 2 attack, 3 idle. */
export function unitMaterial(cavalry: boolean) {
  const mat = new THREE.MeshPhongMaterial({ vertexColors: true, ...ENAMEL }); // enamel, as the 3D armies (look.ts, 2026-10-26)
  mat.onBeforeCompile = (sh) => {
    enamel(sh);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
attribute float part; attribute float aTc; attribute vec2 aAnim; attribute vec3 aBody;
vec3 rotX(vec3 p, float a, float pivotY) { p.y -= pivotY; float c = cos(a), s = sin(a); p = vec3(p.x, p.y * c - p.z * s, p.y * s + p.z * c); p.y += pivotY; return p; }`)
      .replace('#include <color_vertex>', `vColor = mix(color.rgb * mix(vec3(1.0), aBody, step(0.999, color.r * color.g * color.b)), instanceColor.rgb, aTc);`)
      .replace('#include <begin_vertex>', `vec3 transformed = vec3(position);
      float ph = aAnim.x, st = aAnim.y;
      bool walking = st < 1.5;
      float legPivot = ${cavalry ? '0.6' : '0.55'};
      if (part > 0.5 && part < 2.5) transformed = rotX(transformed, walking ? sin(ph) * ${cavalry ? '0.55' : '0.7'} * (part < 1.5 ? 1.0 : -1.0) : 0.0, legPivot);
      if (part > 2.5) transformed = rotX(transformed, walking ? sin(ph) * 0.2 : -abs(sin(ph)) * 1.3, ${cavalry ? '1.45' : '0.95'});
      if (walking) transformed.y += abs(sin(ph)) * 0.06;`);
  };
  // the two materials' onBeforeCompile share one source text (the numbers differ by closure), and three keys its program
  // cache on that text: without a key of their own the riders could get the footmen's program (2026-10-26)
  mat.customProgramCacheKey = () => `unit-${cavalry}`;
  return mat;
}

export const CLASS_IDS = CLASSES;
